import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  serverTimestamp,
} from 'firebase/firestore'
import { useAuth } from '../context/useAuth'
import { useDisplayName } from './useDisplayName'
import { useTimeline } from '../context/useTimeline'
import { useWorkspace } from '../context/useWorkspace'
import { WORKSPACE_DEFAULT_CHECKLIST_ID } from '../context/TimelineContext'
import { firestoreDb } from '../services/firebase'
import {
  assignChecklistItem,
  updateChecklistFields,
  updateChecklistWorkflowStatus,
  workflowStatusToChecklistBoardStatus,
} from '../services/checklistMutationService'
import {
  projectLegacyChecklistTypeToNew,
} from '../services/checklistViewAdapter'
import {
  refreshChecklistCanvasProjectionDisplay,
} from '../services/checklistCanvasProjectionService'
import { writeChecklistUpdate } from '../services/checklistWriter'
import { moveChecklist } from '../services/elementRestructureService'
import { moveChecklistToBin } from '../services/checklistBinService'
import { subscribeChecklistItemProjection } from '../services/checklistReader'
import {
  applyDetailEditToPage,
  isChecklistDetailVersionedField,
  isChecklistRecurringScheduleField,
  pickChecklistDetailVersionPatch,
  setChecklistDetailPatchOnPage,
} from '../services/checklistDetailVersionService'
import { ensurePageDetailVersionForKey } from '../services/checklistBookService'
import { subscribeWorkspaceUserDirectory } from '../services/workspaceUserDirectory'
import {
  createDeltaOperationId,
  ChecklistBlockerError,
} from '../services/stripboardDeltaService'
import {
  evaluateBlockers,
  nextStatusAfterAssignment,
  type BlockerEntry,
} from '../services/checklistStatusMachine'
import { ctaSectionFor } from '../types/checklistBlockerCodes'
import { bookIdFor } from '../types/checklistBooks'
import { parseWorkspaceRole } from '../utils/workspacePermissions'
import { getWorkspaceOwnerIds } from '../utils/workspaceOwners'
import { normalizeChecklistInputLimits } from '../utils/checklistLimits'
import { isPersonalWorkspace } from '../utils/workspaceKind'
import { getCompletionBlockReasons } from '../utils/checklistNodeMeta'
import { buildLockGraph, resolveLock } from '../services/checklistLockService'
import { useChecklistLockState, type LockTarget } from './useChecklistLockState'
import type { RepeatPagesInput } from '../services/repeat/types'
import { useWorkspaceDay } from './useWorkspaceDay'
import {
  getChecklistOperationalAccess,
  type ChecklistBaseOperationalPermissions,
} from '../utils/checklistOperationalPermissions'
import type {
  ChecklistItem,
  ChecklistMemberOption,
  ChecklistWorkflowStatus,
} from '../types/schema'
import type {
  ChecklistDetailController,
  ChecklistDetailControllerNotice,
  ChecklistDetailControllerStatus,
  ChecklistParentSaveOutcome,
} from '../types/checklistDetailController'
import {
  evaluateChecklistParent,
  type ChecklistWindow,
} from '../utils/checklistParentRules'

const normalizeWeightage = (weightage: unknown) =>
  typeof weightage === 'number' && Number.isFinite(weightage) && weightage > 1
    ? Math.round(weightage)
    : 1

const readCurrentCoordinates = () => new Promise<{ lat: number; lng: number }>((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error('Location check is not available in this browser.'))
    return
  }
  navigator.geolocation.getCurrentPosition(
    (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
    () => reject(new Error('Location check failed. Allow browser location access and retry.')),
    { enableHighAccuracy: true, maximumAge: 0, timeout: 12_000 },
  )
})

const getDistanceMeters = (first: { lat: number; lng: number }, second: { lat: number; lng: number }) => {
  const radiusMeters = 6_371_000
  const toRadians = (value: number) => (value * Math.PI) / 180
  const deltaLat = toRadians(second.lat - first.lat)
  const deltaLng = toRadians(second.lng - first.lng)
  const a = Math.sin(deltaLat / 2) ** 2
    + Math.cos(toRadians(first.lat)) * Math.cos(toRadians(second.lat)) * Math.sin(deltaLng / 2) ** 2
  return radiusMeters * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const normalizeMemberRole = (role: string): ChecklistMemberOption['role'] =>
  parseWorkspaceRole(role) ?? 'VIEWER'

export const useChecklistDetailController = (
  itemId: string | null,
  controllerOpts: { pageKey?: string | null; pages?: RepeatPagesInput } = {},
): ChecklistDetailController => {
  const { currentUser, userProfile } = useAuth()
  const currentDisplayName = useDisplayName(userProfile ?? currentUser)
  const {
    checklistItems,
    elements,
    applyStripboardDelta,
    isLoading: timelineLoading,
    isPublicReadOnly,
    canPerformOnNode,
  } = useTimeline()
  const { activeWorkspaceId, activeRole, workspaces, canPerform } = useWorkspace()
  const { timeZone, todayIso, weekKey } = useWorkspaceDay(activeWorkspaceId)
  const [workspaceMembers, setWorkspaceMembers] = useState<ChecklistMemberOption[]>([])
  const [notice, setNotice] = useState<ChecklistDetailControllerNotice>(null)
  const [projectedItem, setProjectedItem] = useState<ChecklistItem | null>(null)
  const [subscribedProjectionKey, setSubscribedProjectionKey] = useState<string | null>(null)
  const projectionKey = `${itemId ?? ''}:${controllerOpts.pageKey ?? ''}`
  const [prevItemId, setPrevItemId] = useState(itemId)
  if (prevItemId !== itemId) {
    setPrevItemId(itemId)
    setProjectedItem(null)
  }
  const projectedItemLoading = Boolean(itemId && subscribedProjectionKey !== projectionKey)
  const [pendingStatus, setPendingStatusState] = useState<ChecklistWorkflowStatus | null>(null)
  const [serverBlockers, setServerBlockers] = useState<BlockerEntry[]>([])
  const isCommittingPendingRef = useRef(false)

  const setPendingStatus = useCallback((status: ChecklistWorkflowStatus | null) => {
    setPendingStatusState(status)
    setServerBlockers([])
  }, [])

  useEffect(() => {
    if (!itemId) return
    const key = `${itemId}:${controllerOpts.pageKey ?? ''}`
    const unsubscribe = subscribeChecklistItemProjection(
      { itemId, pageKey: controllerOpts.pageKey ?? null },
      (nextItem) => {
        setProjectedItem(nextItem)
        setSubscribedProjectionKey(key)
      },
    )
    return unsubscribe
  }, [itemId, controllerOpts.pageKey])

  const item = useMemo(
    () => projectedItem ?? (itemId ? checklistItems.find((candidate) => candidate.id === itemId) ?? null : null),
    [checklistItems, projectedItem, itemId],
  )
  const workspaceId = item?.workspaceId ?? activeWorkspaceId ?? ''
  const [prevWorkspaceId, setPrevWorkspaceId] = useState(workspaceId)
  if (prevWorkspaceId !== workspaceId) {
    setPrevWorkspaceId(workspaceId)
    setWorkspaceMembers([])
  }
  const activeWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === workspaceId || workspace.id === activeWorkspaceId) ?? null,
    [activeWorkspaceId, workspaceId, workspaces],
  )
  const checklistLimits = useMemo(
    () => normalizeChecklistInputLimits(activeWorkspace?.checklistInputLimits, isPersonalWorkspace(activeWorkspace)),
    [activeWorkspace],
  )

  useEffect(() => {
    if (!workspaceId) return
    const unsubscribe = subscribeWorkspaceUserDirectory(
      workspaceId,
      (members) => {
        const nextMembers = members.map((member): ChecklistMemberOption => ({
          userId: member.uid,
          displayName: member.displayName,
          email: member.email,
          role: normalizeMemberRole(member.role),
        }))
        const ownerIds = getWorkspaceOwnerIds(activeWorkspace)
        for (const ownerId of ownerIds) {
          if (!nextMembers.some((member) => member.userId === ownerId)) {
            nextMembers.push({
              userId: ownerId,
              displayName: 'Workspace admin',
              email: '',
              role: 'OWNER',
            })
          }
        }
        setWorkspaceMembers(nextMembers)
      },
      (error) => {
        console.warn('[useChecklistDetailController] workspace member load failed', error)
        setWorkspaceMembers([])
      },
    )
    return unsubscribe
  }, [activeWorkspace, workspaceId])

  const parentCandidates = useMemo<typeof elements>(() => {
    if (!workspaceId) return []
    return elements.filter((element) => {
      if (!element) return false
      if (element.deletedAt) return false
      if (element.type !== 'GOAL' && element.type !== 'TASK' && element.type !== 'SUBTASK') return false
      return true
    })
  }, [elements, workspaceId])

  const boardNodes = useMemo(() => {
    if (!item) return []
    return checklistItems
      .filter((candidate) => candidate.workspaceId === item.workspaceId)
      .filter((candidate) => !candidate.deletedAt)
      .filter((candidate) => !candidate.isRecurrenceInstance)
  }, [checklistItems, item])

  const baseOperationalPermissions: ChecklistBaseOperationalPermissions = useMemo(() => ({
    assign: item ? canPerformOnNode('checklist.assign', item.id) : canPerform('checklist.assign'),
    status: item ? canPerformOnNode('checklist.change_status', item.id) : canPerform('checklist.change_status'),
    proof: item ? canPerformOnNode('checklist.manage_proof', item.id) : canPerform('checklist.manage_proof'),
    memo: item ? canPerformOnNode('checklist.manage_memo', item.id) : canPerform('checklist.manage_memo'),
    comment: item ? canPerformOnNode('checklist.comment', item.id) : canPerform('checklist.comment'),
    locationCheckin: item ? canPerformOnNode('checklist.location_checkin', item.id) : canPerform('checklist.location_checkin'),
  }), [canPerform, canPerformOnNode, item])

  const isReadOnly = isPublicReadOnly || !currentUser
  const canEdit = !isReadOnly && (item ? canPerformOnNode('checklist.edit_structure', item.id) : canPerform('checklist.edit_structure'))
  const canDelete = !isReadOnly && (item ? canPerformOnNode('checklist.delete', item.id) : canPerform('checklist.delete'))
  const canInteractWithChecklist = !isPublicReadOnly

  const operationalAccess = useMemo(() => item ? getChecklistOperationalAccess({
    item,
    currentUserId: currentUser?.uid,
    activeRole,
    isReadOnly,
    basePermissions: baseOperationalPermissions,
  }) : null, [activeRole, baseOperationalPermissions, currentUser?.uid, isReadOnly, item])

  const effectivePeriodKey = controllerOpts.pageKey ?? (item?.checklistType === 'WEEKLY' ? weekKey : todayIso)
  const targetItemId = item?.id
  const detailTargets = useMemo<LockTarget[]>(() => {
    if (!targetItemId) return []
    return [{ itemId: targetItemId, periodKey: effectivePeriodKey }]
  }, [effectivePeriodKey, targetItemId])

  const lockMap = useChecklistLockState(checklistItems, detailTargets, {
    workspaceId: activeWorkspaceId,
    timeZone,
  })

  const pendingBlockers = useMemo<BlockerEntry[]>(() => {
    if (!pendingStatus || !item) return []
    const graph = buildLockGraph(checklistItems)
    const effectivePages = controllerOpts.pages ?? lockMap.pages
    const lock = resolveLock(item, effectivePeriodKey, graph, effectivePages, undefined, timeZone)
    const incompleteDepIds = lock.isLocked
      ? (lock.reasons.map((r) => r.targetId).filter(Boolean) as string[])
      : []
    const evalTarget = {
      ...item,
      dependencyAlertNodeIds: incompleteDepIds,
    }
    const clientEval = evaluateBlockers(evalTarget, { uid: currentUser?.uid, role: activeRole }, pendingStatus)
    if (!clientEval.ok) {
      return clientEval.blockers
    }
    if (serverBlockers.length > 0) {
      return serverBlockers
    }
    return []
  }, [activeRole, checklistItems, controllerOpts.pages, currentUser?.uid, effectivePeriodKey, item, lockMap, pendingStatus, serverBlockers, timeZone])

  const clearNotice = useCallback(() => setNotice(null), [])
  const showNotice = useCallback((message: string, kind: NonNullable<ChecklistDetailControllerNotice>['kind'] = 'warning') => {
    setNotice({ kind, message })
  }, [])
  const primeProjectedItem = useCallback((
    target: ChecklistItem,
    updates: Partial<ChecklistItem> = {},
  ) => {
    setProjectedItem((current) => {
      const basis = current?.id === target.id ? current : itemId === target.id ? target : null
      return basis ? { ...basis, ...updates } : current
    })
  }, [itemId])
  const refreshProjectionDisplay = useCallback((
    target: ChecklistItem,
    updates: Partial<ChecklistItem> = {},
  ) => {
    void refreshChecklistCanvasProjectionDisplay(firestoreDb, {
      ...target,
      ...updates,
    }, {
      updatedAt: serverTimestamp(),
      lastUpdatedBy: currentUser?.uid ?? null,
      lastUpdatedByName: currentDisplayName || currentUser?.email || null,
    }).catch(() => undefined)
  }, [currentDisplayName, currentUser])
  const actor = useMemo(
    () => ({
      id: currentUser?.uid,
      name: currentDisplayName || currentUser?.email || null,
    }),
    [currentDisplayName, currentUser],
  )
  const recurringScopedPageKey = useMemo(() => {
    if (!item) return null
    return item.checklistType === 'DAILY' || item.checklistType === 'WEEKLY'
      ? controllerOpts.pageKey ?? null
      : null
  }, [item, controllerOpts.pageKey])

  const saveDraft = useCallback(async (
    target: ChecklistItem,
    draft: Partial<ChecklistItem>,
    saveOpts: { scope?: 'single' | 'future' | 'global' | null } = {},
  ) => {
    if (!canEdit) return

    const rawEntries = Object.entries(draft).filter(
      ([key, value]) => value !== undefined && !Object.is(target[key as keyof ChecklistItem], value),
    )
    if (rawEntries.length === 0) return
    const updates = Object.fromEntries(rawEntries) as Record<string, unknown>
    const titleUpdate = typeof updates.title === 'string'
      ? updates.title.trim().slice(0, checklistLimits.titleMaxLength)
      : null
    const recurringScoped = (target.checklistType === 'DAILY' || target.checklistType === 'WEEKLY')
      && Boolean(recurringScopedPageKey)

    if (!recurringScoped) {
      const directUpdates = Object.fromEntries(
        Object.entries(updates).filter(([key]) => key !== 'title'),
      )
      if (titleUpdate && titleUpdate !== target.title) {
        await updateChecklistFields(firestoreDb, {
          item: { ...target, title: titleUpdate },
          updates: { title: titleUpdate },
          actor,
          summary: `Renamed checklist item from ${target.title} to ${titleUpdate}`,
          metadata: { changedFields: ['title'] },
        })
      }
      if (Object.keys(directUpdates).length > 0) {
        await updateChecklistFields(firestoreDb, {
          item: target,
          updates: directUpdates,
          actor,
          summary: `Updated ${Object.keys(directUpdates).join(', ')}`,
          metadata: { changedFields: Object.keys(directUpdates) },
        })
      }
      refreshProjectionDisplay(target, {
        ...(titleUpdate ? { title: titleUpdate } : {}),
        ...(directUpdates as Partial<ChecklistItem>),
      })
      primeProjectedItem(target, {
        ...(titleUpdate ? { title: titleUpdate } : {}),
        ...(directUpdates as Partial<ChecklistItem>),
      })
      return
    }

    // Schedule fields (scheduleStartDate/EndDate, hasTimeWindow, time-of-day,
    // legacy start/end) belong to the recurrence CADENCE, not to any single
    // occurrence — they are global-by-nature for DAILY/WEEKLY items. They
    // are NOT versioned-per-page, and prior behavior excluded them from both
    // liveUpdates AND the versioned patch, resulting in a silent drop where
    // Save appeared to succeed but no schedule write reached Firestore.
    // Route them through liveUpdates → updateChecklistFields → definition doc
    // (mutationService keeps schedule fields in the definition-forwrite bucket
    // via the !isChecklistRecurringScheduleField carve-out at line 241).
    const liveUpdates: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(updates)) {
      if (key === 'title') continue
      if (recurringScoped
        && isChecklistDetailVersionedField(key)
        && !isChecklistRecurringScheduleField(key)) continue
      liveUpdates[key] = value
    }

    if (titleUpdate && titleUpdate !== target.title) {
      await updateChecklistFields(firestoreDb, {
        item: { ...target, title: titleUpdate },
        updates: { title: titleUpdate },
        actor,
        summary: `Renamed checklist item from ${target.title} to ${titleUpdate}`,
        metadata: { changedFields: ['title'] },
      })
    }

    if (recurringScoped) {
      const versionedPatch = pickChecklistDetailVersionPatch(updates)
      if (Object.keys(versionedPatch).length > 0 && actor.id) {
        const type = projectLegacyChecklistTypeToNew(target.checklistType)
        const bookId = bookIdFor(target.workspaceId, type)
        await ensurePageDetailVersionForKey(
          bookId,
          target.workspaceId,
          type,
          recurringScopedPageKey!,
          { uid: actor.id, displayName: actor.name },
        )
        if (saveOpts.scope === 'single') {
          await setChecklistDetailPatchOnPage(
            bookId,
            recurringScopedPageKey!,
            target.id,
            versionedPatch,
            { uid: actor.id, displayName: actor.name },
          )
        } else {
          await applyDetailEditToPage(
            bookId,
            target.workspaceId,
            recurringScopedPageKey!,
            target.id,
            versionedPatch,
            { uid: actor.id, displayName: actor.name },
          )
        }
      }
    }

    if (Object.keys(liveUpdates).length > 0) {
      await updateChecklistFields(firestoreDb, {
        item: target,
        updates: liveUpdates,
        actor,
        pageKey: recurringScopedPageKey,
        scope: saveOpts.scope ?? undefined,
        action: 'CHECKLIST_UPDATED',
        summary: `Updated ${Object.keys(liveUpdates).join(', ')}`,
        metadata: { changedFields: Object.keys(liveUpdates) },
      })
    }

    refreshProjectionDisplay(target, {
      ...(titleUpdate ? { title: titleUpdate } : {}),
      ...(updates as Partial<ChecklistItem>),
    })
    primeProjectedItem(target, {
      ...(titleUpdate ? { title: titleUpdate } : {}),
      ...(updates as Partial<ChecklistItem>),
    })
  }, [
    actor,
    canEdit,
    checklistLimits.titleMaxLength,
    primeProjectedItem,
    recurringScopedPageKey,
    refreshProjectionDisplay,
  ])

  const updateTitle = useCallback(async (target: ChecklistItem, title: string) => {
    const nextTitle = title.trim().slice(0, checklistLimits.titleMaxLength)
    if (nextTitle === target.title || !canEdit) return
    await updateChecklistFields(firestoreDb, {
      item: { ...target, title: nextTitle },
      updates: { title: nextTitle },
      actor,
      summary: `Renamed checklist item from ${target.title} to ${nextTitle}`,
      metadata: { changedFields: ['title'] },
    })
    refreshProjectionDisplay(target, { title: nextTitle })
  }, [actor, canEdit, checklistLimits.titleMaxLength, refreshProjectionDisplay])

  const updateWeightage = useCallback(async (target: ChecklistItem, weightage: string) => {
    if (!canEdit) return
    const nextWeightage = Math.min(checklistLimits.weightageMax, normalizeWeightage(Number(weightage)))
    if (nextWeightage === normalizeWeightage(target.weightage)) return
    await updateChecklistFields(firestoreDb, {
      item: target,
      updates: { weightage: nextWeightage },
      actor,
      summary: `Changed weight from ${normalizeWeightage(target.weightage)} to ${nextWeightage}`,
      metadata: { changedFields: ['weightage'] },
    })
    refreshProjectionDisplay(target, { weightage: nextWeightage })
  }, [actor, canEdit, checklistLimits.weightageMax, refreshProjectionDisplay])

  const updateVerification = useCallback(async (
    target: ChecklistItem,
    updates: Partial<ChecklistItem>,
    notRequiredScope?: 'FROM_PERIOD' | 'CLEAR' | null,
  ) => {
    if (!canInteractWithChecklist) return
    const changedKeys = Object.keys(updates)
    const access = getChecklistOperationalAccess({
      item: target,
      currentUserId: currentUser?.uid,
      activeRole,
      isReadOnly,
      basePermissions: baseOperationalPermissions,
    }).permissions
    const isCompletionUpdate = updates.workflowStatus === 'COMPLETED' || updates.isCompleted === true
    const permitted = changedKeys.some((key) => ['workflowStatus', 'isCompleted'].includes(key))
      ? isCompletionUpdate ? access.complete : access.status
      : changedKeys.includes('verifiedCoordinates')
        ? access.locationCheckin
        : changedKeys.some((key) => ['proofFileUrl', 'proofFileName', 'proofFileStoragePath', 'proofUploadedById', 'proofUploadedByName', 'proofUploadedAt'].includes(key))
          ? access.proof
          : changedKeys.some((key) => ['proofVideoUrl', 'voiceMemoBlobUrl', 'memoStoragePath', 'memoFileName', 'memoContentType', 'memoUploadedById', 'memoUploadedByName', 'memoUploadedAt'].includes(key))
            ? access.memo
            : canEdit
    if (!permitted) {
      showNotice('You do not have permission to update this checklist item.')
      return
    }

    const directUpdates: Partial<ChecklistItem> = { ...updates }
    let expectedVersion = target.recordVersion
    let boardStatus = target.boardStatus

    if ('scheduleStartDate' in updates || 'scheduleEndDate' in updates || 'startDate' in updates || 'endDate' in updates) {
      const result = await applyStripboardDelta({
        operationId: createDeltaOperationId('checklist-dates', target.id),
        operationType: 'DATE_UPDATE',
        recordId: target.id,
        recordKind: 'CHECKLIST',
        sourceStatus: boardStatus,
        destinationStatus: boardStatus,
        expectedVersion,
        startDate: updates.startDate ?? updates.scheduleStartDate ?? target.startDate ?? target.scheduleStartDate ?? null,
        endDate: updates.endDate ?? updates.scheduleEndDate ?? target.endDate ?? target.scheduleEndDate ?? null,
      })
      expectedVersion = result.recordVersion
      boardStatus = result.boardStatus
      delete directUpdates.startDate
      delete directUpdates.endDate
      delete directUpdates.scheduleStartDate
      delete directUpdates.scheduleEndDate
    }

    if ('priority' in updates || 'urgencyStars' in updates || 'importanceStars' in updates) {
      const priority = updates.priority ?? Math.ceil(((updates.urgencyStars ?? target.urgencyStars ?? 1) + (updates.importanceStars ?? target.importanceStars ?? 1)) / 2)
      const result = await applyStripboardDelta({
        operationId: createDeltaOperationId('checklist-priority', target.id),
        operationType: 'PRIORITY_UPDATE',
        recordId: target.id,
        recordKind: 'CHECKLIST',
        sourceStatus: boardStatus,
        destinationStatus: boardStatus,
        expectedVersion,
        priority,
      })
      expectedVersion = result.recordVersion
      delete directUpdates.priority
    }

    if ('workflowStatus' in updates) {
      const result = await updateChecklistWorkflowStatus({
        item: target,
        workflowStatus: updates.workflowStatus,
        coordinates: updates.verifiedCoordinates ?? null,
        runDelta: applyStripboardDelta,
        actor,
        periodKey: recurringScopedPageKey,
        pageKey: recurringScopedPageKey,
        notRequiredScope,
      })
      refreshProjectionDisplay(target, {
        ...updates,
        boardStatus: result.boardStatus,
        ...(notRequiredScope === 'FROM_PERIOD' && recurringScopedPageKey ? { notRequiredFrom: recurringScopedPageKey } : {}),
        ...(notRequiredScope === 'CLEAR' ? { notRequiredFrom: null } : {}),
      })
      return
    }

    if (Object.keys(directUpdates).length > 0) {
      await updateChecklistFields(firestoreDb, {
        item: target,
        updates: {
          ...directUpdates,
          boardStatus,
          recordVersion: expectedVersion,
        } as Record<string, unknown>,
        actor,
        pageKey: recurringScopedPageKey,
        action: updates.proofFileUrl !== undefined || updates.verifiedCoordinates !== undefined
          ? 'CHECKLIST_PROOF_UPDATED'
          : 'CHECKLIST_UPDATED',
        summary: updates.proofFileUrl !== undefined || updates.verifiedCoordinates !== undefined
          ? 'Updated completion verification'
          : `Updated ${Object.keys(updates).join(', ')}`,
        metadata: { changedFields: Object.keys(updates) },
      })
      refreshProjectionDisplay(target, {
        ...directUpdates,
        boardStatus,
        recordVersion: expectedVersion,
      } as Partial<ChecklistItem>)
    }
  }, [activeRole, actor, applyStripboardDelta, baseOperationalPermissions, canEdit, canInteractWithChecklist, currentUser?.uid, isReadOnly, recurringScopedPageKey, refreshProjectionDisplay, showNotice])

  const updateStatus = useCallback(async (
    target: ChecklistItem,
    status: ChecklistWorkflowStatus,
    opts?: { notRequiredScope?: 'FROM_PERIOD' | 'CLEAR' | null },
  ) => {
    const access = getChecklistOperationalAccess({
      item: target,
      currentUserId: currentUser?.uid,
      activeRole,
      isReadOnly,
      basePermissions: baseOperationalPermissions,
    })
    if (status === 'COMPLETED' ? !access.permissions.complete : !access.permissions.status) {
      showNotice(status === 'COMPLETED'
        ? access.completionDisabledReason ?? access.workDisabledReason ?? 'Your role cannot complete this checklist item.'
        : access.workDisabledReason ?? 'Your role cannot change this checklist status.')
      return
    }

    const graph = buildLockGraph(checklistItems)
    const effectivePages = controllerOpts.pages ?? lockMap.pages
    const lock = resolveLock(target, effectivePeriodKey, graph, effectivePages, undefined, timeZone)
    const incompleteDepIds = lock.isLocked
      ? (lock.reasons.map((r) => r.targetId).filter(Boolean) as string[])
      : []
    const evalTarget = {
      ...target,
      dependencyAlertNodeIds: incompleteDepIds,
    }
    const evalResult = evaluateBlockers(evalTarget, { uid: currentUser?.uid, role: activeRole }, status)
    if (!evalResult.ok) {
      setPendingStatusState(status)
      setServerBlockers([])
      return
    }

    const effectiveNotRequiredScope =
      opts?.notRequiredScope !== undefined
        ? opts.notRequiredScope
        : status !== 'NOT_REQUIRED' && target.notRequiredFrom
          ? 'CLEAR'
          : null

    try {
      if (status !== 'COMPLETED') {
        await updateVerification(
          target,
          {
            workflowStatus: status,
            ...(target.isCompleted ? { isCompleted: false } : {}),
          },
          effectiveNotRequiredScope,
        )
        setPendingStatusState((current) => (current === status ? null : current))
        setServerBlockers([])
        return
      }

      const openBlockerTitles = lock.isLocked
        ? lock.reasons.map((r) => r.title ?? r.targetId ?? 'Dependency').filter(Boolean)
        : []
      const reasons = getCompletionBlockReasons(target, { openBlockerTitles })
      if (reasons.length > 0) {
        showNotice(reasons.join(' • '))
        return
      }

      if (target.requiresLocationPin && target.targetCoordinates) {
        if (!access.permissions.locationCheckin) {
          showNotice(access.completionDisabledReason ?? access.workDisabledReason ?? 'Your role cannot submit a location check-in.')
          return
        }
        const verifiedCoordinates = await readCurrentCoordinates()
        if (getDistanceMeters(target.targetCoordinates, verifiedCoordinates) > 50) {
          showNotice('Verification Rejected: you must be physically on-site to mark this complete.')
          return
        }
        await updateVerification(target, { workflowStatus: 'COMPLETED', isCompleted: true, verifiedCoordinates })
        setPendingStatusState((current) => (current === status ? null : current))
        setServerBlockers([])
        return
      }

      await updateVerification(target, { workflowStatus: 'COMPLETED', isCompleted: true })
      setPendingStatusState((current) => (current === status ? null : current))
      setServerBlockers([])
    } catch (error) {
      if (error instanceof ChecklistBlockerError) {
        setPendingStatusState(status)
        setServerBlockers(
          error.blockers.map((b) => ({
            code: b.code,
            message: b.message,
            ctaSection: ctaSectionFor(b.code),
          })),
        )
        return
      }
      const isVersionConflict = (error as { details?: { code?: string } })?.details?.code === 'VERSION_CONFLICT'
        || ((error as { code?: string })?.code === 'functions/aborted' || (error as { code?: string })?.code === 'aborted')
          && /source lane|record changed/i.test(String((error as { message?: string })?.message))
        || /source lane|record changed/i.test(String((error as { message?: string })?.message))

      if (isVersionConflict) {
        showNotice('This item was updated by someone else. Refreshing…')
        try {
          const { refetchChecklistItemHead } = await import('../services/checklistReader')
          const fresh = await refetchChecklistItemHead(target.id)
          if (fresh) {
            const updates = {
              boardStatus: fresh.boardStatus,
              workflowStatus: fresh.workflowStatus,
              isCompleted: fresh.workflowStatus === 'COMPLETED',
              recordVersion: fresh.recordVersion,
            }
            primeProjectedItem(target, updates)
            refreshProjectionDisplay(target, updates)
          }
        } catch {
          // ignore refetch failure
        }
        return
      }
      const errorCode = (error as { code?: string })?.code ?? (error as { details?: { code?: string } })?.details?.code ?? 'UNKNOWN'
      showNotice(`Could not change status: ${errorCode}`)
    }
  }, [activeRole, baseOperationalPermissions, checklistItems, controllerOpts.pages, currentUser?.uid, effectivePeriodKey, isReadOnly, lockMap.pages, primeProjectedItem, refreshProjectionDisplay, showNotice, timeZone, updateVerification])

  useEffect(() => {
    if (!item || !pendingStatus || pendingBlockers.length > 0) return
    if (isCommittingPendingRef.current) return

    let isMounted = true
    isCommittingPendingRef.current = true

    void (async () => {
      try {
        await updateStatus(item, pendingStatus)
      } catch (error) {
        if (isMounted && error instanceof ChecklistBlockerError) {
          setServerBlockers(
            error.blockers.map((b) => ({
              code: b.code,
              message: b.message,
              ctaSection: ctaSectionFor(b.code),
            })),
          )
        }
      } finally {
        isCommittingPendingRef.current = false
      }
    })()

    return () => {
      isMounted = false
    }
  }, [item, pendingStatus, pendingBlockers.length, updateStatus])

  const updateAssignment = useCallback(async (
    target: ChecklistItem,
    assignee: ChecklistMemberOption | null,
    opts: { scope?: 'single' | 'future' | 'global' | null; pageKey?: string | null } = {},
  ) => {
    if (!canInteractWithChecklist || !currentUser) return
    const access = getChecklistOperationalAccess({
      item: target,
      currentUserId: currentUser.uid,
      activeRole,
      isReadOnly,
      basePermissions: baseOperationalPermissions,
    })
    if (!access.permissions.assign) {
      showNotice(access.workDisabledReason ?? 'You cannot assign this checklist item.')
      return
    }
    const isRecurring = target.checklistType === 'DAILY' || target.checklistType === 'WEEKLY'
    const effectivePageKey = opts.pageKey ?? (isRecurring ? recurringScopedPageKey : null)
    // On recurring items, if the caller supplied a scoped page key but
    // didn't say the scope, default to 'future' — it's the safer of the two
    // scoped choices (past assignments frozen, current + future updated).
    const effectiveScope: 'single' | 'future' | 'global' | undefined = opts.scope === null
      ? undefined
      : opts.scope
        ?? (isRecurring && effectivePageKey ? 'future' : undefined)
    try {
      await assignChecklistItem(firestoreDb, {
        item: target,
        assignee,
        actor: {
          id: currentUser.uid,
          name: currentDisplayName || currentUser.email || 'Teammate',
        },
        pageKey: effectivePageKey,
        scope: effectiveScope ?? undefined,
      })
    } catch (error) {
      const code = (error as { code?: string })?.code ?? 'unknown'
      console.error('[useChecklistDetailController] assignment failed', {
        code,
        itemId: target.id,
        checklistType: target.checklistType ?? 'INDIVIDUAL',
        scope: effectiveScope ?? 'global',
        pageKey: effectivePageKey,
        error,
      })
      showNotice(code === 'permission-denied'
        ? 'You don\'t have permission to change this assignee.'
        : 'Could not update the assignee. Try again.')
      return
    }
    const nextStatus = nextStatusAfterAssignment(target.workflowStatus, Boolean(assignee?.userId))
    // Projection display refresh — for scoped assigns, don't update the
    // top-level projection (that displays the definition's assignee); the
    // page-projection subscription will pick up the detailPatch on next
    // snapshot.
    if (effectiveScope === 'global' || !effectiveScope) {
      refreshProjectionDisplay(target, {
        assigneeId: assignee?.userId ?? null,
        assigneeName: assignee?.displayName ?? null,
        ...(nextStatus ? {
          workflowStatus: nextStatus,
          boardStatus: workflowStatusToChecklistBoardStatus(nextStatus),
          isCompleted: false,
        } : {}),
      })
    }
  }, [activeRole, baseOperationalPermissions, canInteractWithChecklist, currentDisplayName, currentUser, isReadOnly, recurringScopedPageKey, refreshProjectionDisplay, showNotice])

  const deleteItem = useCallback(async (target: ChecklistItem) => {
    if (!canDelete) {
      showNotice('You do not have permission to delete checklist items.')
      return
    }
    const ctrlActor = { uid: currentUser?.uid ?? '', displayName: currentDisplayName || currentUser?.email || null }
    let legacyChecklistUpdate
    if (target.parentContainerId !== WORKSPACE_DEFAULT_CHECKLIST_ID) {
      const parent = elements.find((element) => element.id === target.parentContainerId)
      if (parent?.checklist?.length) {
        legacyChecklistUpdate = parent.checklist.filter((entry) => entry.id !== target.id)
      }
    }

    await moveChecklistToBin({
      targetId: target.id,
      targetTitle: target.title,
      workspaceId: target.workspaceId,
      board: target.board ?? 'DASHBOARD',
      deletedFromBoard: target.deletedFromBoard,
      parentContainerId: target.parentContainerId,
      currentUser: ctrlActor,
      currentDisplayName: ctrlActor.displayName,
      firestoreDb,
      legacyChecklistUpdate,
    })
  }, [canDelete, currentDisplayName, currentUser, elements, showNotice])

  const updateParent = useCallback(async (
    target: ChecklistItem,
    nextParentIdInput: string | null,
    parentOpts: { window?: ChecklistWindow | null } = {},
  ): Promise<ChecklistParentSaveOutcome> => {
    if (!canEdit) {
      return { status: 'rejected', reason: 'You do not have permission to change this checklist\'s parent.' }
    }
    const workspaceDefaultId = WORKSPACE_DEFAULT_CHECKLIST_ID
    const resolvedParentId = nextParentIdInput ?? workspaceDefaultId
    const parentElement = resolvedParentId === workspaceDefaultId
      ? null
      : elements.find((element) => element.id === resolvedParentId) ?? null

    const currentWindow: ChecklistWindow | null = target.scheduleStartDate && target.scheduleEndDate
      ? { start: target.scheduleStartDate.slice(0, 10), end: target.scheduleEndDate.slice(0, 10) }
      : null
    const windowOverride = parentOpts.window ?? undefined
    const effectiveWindow: ChecklistWindow | null = windowOverride === undefined
      ? currentWindow
      : windowOverride

    const checklistType = target.checklistType === 'DAILY' || target.checklistType === 'WEEKLY'
      ? target.checklistType
      : 'INDIVIDUAL' as const

    const verdict = evaluateChecklistParent({
      checklistType,
      window: effectiveWindow,
      parent: parentElement,
      parentId: resolvedParentId,
      workspaceDefaultContainerId: workspaceDefaultId,
    })

    if (!verdict.ok) {
      showNotice(verdict.reason, 'warning')
      return verdict.clampTo
        ? { status: 'needs-clamp', clampTo: verdict.clampTo, reason: verdict.reason }
        : { status: 'rejected', reason: verdict.reason }
    }

    const patch: Record<string, unknown> = { parentContainerId: resolvedParentId }
    if (windowOverride !== undefined && windowOverride) {
      patch.scheduleStartDate = windowOverride.start
      patch.scheduleEndDate = windowOverride.end
    }
    // DAILY/WEEKLY carry a recurringWindow mirror off their parent — refresh
    // it here so the item stays in sync after a reparent. Individual items
    // don't use this field.
    if ((checklistType === 'DAILY' || checklistType === 'WEEKLY') && parentElement) {
      const parentStart = (parentElement.startDate as string | null | undefined)?.slice(0, 10)
      const parentEnd = (parentElement.endDate as string | null | undefined)?.slice(0, 10)
      if (parentStart && parentEnd) {
        patch.recurringWindow = {
          parentElementId: parentElement.id,
          startDate: parentStart,
          endDate: parentEnd,
          syncedAt: serverTimestamp(),
        }
      }
    }

    try {
      await moveChecklist(
        target.workspaceId,
        [target.id],
        (parentElement?.board as 'VISIONBOARD' | 'DASHBOARD' | undefined) ?? target.board,
        resolvedParentId,
      )
      const otherPatch = { ...patch }
      delete otherPatch.parentContainerId
      if (Object.keys(otherPatch).length > 0) {
        await writeChecklistUpdate(
          target.id,
          otherPatch,
          { uid: currentUser?.uid ?? '', displayName: currentDisplayName || currentUser?.email || null },
        )
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Could not change parent. Try again.'
      showNotice(reason, 'error')
      return { status: 'rejected', reason }
    }

    primeProjectedItem(target, {
      parentContainerId: resolvedParentId,
      ...(patch.scheduleStartDate ? { scheduleStartDate: patch.scheduleStartDate as string } : {}),
      ...(patch.scheduleEndDate ? { scheduleEndDate: patch.scheduleEndDate as string } : {}),
    })
    refreshProjectionDisplay(target, {
      parentContainerId: resolvedParentId,
      ...(patch.scheduleStartDate ? { scheduleStartDate: patch.scheduleStartDate as string } : {}),
      ...(patch.scheduleEndDate ? { scheduleEndDate: patch.scheduleEndDate as string } : {}),
    })
    return { status: 'saved' }
  }, [
    canEdit,
    currentDisplayName,
    currentUser,
    elements,
    primeProjectedItem,
    refreshProjectionDisplay,
    showNotice,
  ])

  const status: ChecklistDetailControllerStatus = !itemId
    ? 'idle'
    : item
      ? 'ready'
      : timelineLoading || projectedItemLoading
        ? 'loading'
        : 'missing'

  return {
    itemId,
    item,
    status,
    notice,
    clearNotice,
    boardNodes,
    workspaceMembers,
    isReadOnly,
    canEdit,
    canDelete,
    operationalAccess,
    updateTitle,
    updateWeightage,
    saveDraft,
    updateVerification,
    updateStatus,
    updateAssignment,
    deleteItem,
    updateParent,
    parentCandidates,
    workspaceDefaultContainerId: WORKSPACE_DEFAULT_CHECKLIST_ID,
    pendingStatus,
    setPendingStatus,
    pendingBlockers,
  }
}
